import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import type { SearchExecution, SearchExecutionListParams } from '@/api/types';
import { AdminPageSections, AutoRefreshToolbar, BulkActionBar, BulkActionButton, CollapsiblePanel } from '@/components/common';
import VehicleFilterDiagnosticsDrawer from '@/components/pipeline/VehicleFilterDiagnosticsDrawer';
import ScrapeAnalyticsPanel from '@/components/pipeline/ScrapeAnalyticsPanel';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import { DataTable, TruncatedCell, type DataTableColumn } from '@/components/table';
import { EmptyState, ErrorBanner, FilterSelect, SourceChips, Toggle, Toolbar } from '@/components/ui';
import StatusBadge from '@/components/ui/StatusBadge';
import { useToast } from '@/components/Toast';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { exportTableCsv } from '@/utils/exportTableCsv';
import { formatTableError } from '@/components/table/formatCellValue';
import PipelineBulkDeleteModal from '@/components/pipeline/PipelineBulkDeleteModal';
import PipelineClearHistoryModal from '@/components/pipeline/PipelineClearHistoryModal';
import { formatApiError } from '@/utils/formatApiError';
import { filterValidUuids } from '@/utils/uuid';
import { SOURCE_FILTER_OPTIONS } from '@/utils/sourceFilterOptions';
import { isOverallPipelineExecution, pipelineSourceLabel } from '@/utils/pipelineDisplay';

const PAGE_SIZE = 50;
const PANEL_SCOPE = 'pipeline-page';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'success', label: 'Success' },
  { value: 'partial', label: 'Partial' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

function formatTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' {
  switch (status) {
    case 'success':
      return 'success';
    case 'running':
    case 'queued':
      return 'info';
    case 'partial':
      return 'warning';
    case 'failed':
      return 'danger';
    case 'skipped':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function ExecutionDetailPanel({
  execution,
  onOpenVehicleFilters,
}: {
  execution: SearchExecution;
  onOpenVehicleFilters?: () => void;
}) {
  const filterDiag = execution.filter_diagnostics || {};
  const scrapeSkipped = Boolean(filterDiag.scrape_skipped) || execution.status === 'skipped';
  const skipReason =
    (typeof filterDiag.scrape_skip_reason === 'string' && filterDiag.scrape_skip_reason) ||
    (typeof filterDiag.mobile_de_skip_reason === 'string' && filterDiag.mobile_de_skip_reason) ||
    null;
  const generatedUrl =
    execution.seed_url ||
    (typeof filterDiag.generated_url === 'string' ? filterDiag.generated_url : null);
  const mobileDeExtraction =
    typeof filterDiag.candidate_listing_url_count === 'number'
      ? {
          anchorHrefs:
            typeof filterDiag.candidate_anchor_count === 'number'
              ? filterDiag.candidate_anchor_count
              : null,
          listingCandidates: filterDiag.candidate_listing_url_count,
          uniqueListings:
            typeof filterDiag.unique_listing_id_count === 'number'
              ? filterDiag.unique_listing_id_count
              : null,
          duplicateListingIds:
            typeof filterDiag.duplicate_listing_id_count === 'number'
              ? filterDiag.duplicate_listing_id_count
              : 0,
          rejectedNonListing:
            typeof filterDiag.rejected_non_listing_count === 'number'
              ? filterDiag.rejected_non_listing_count
              : 0,
        }
      : null;
  const rejectionCount =
    typeof filterDiag.vehicle_filter_rejections === 'number'
      ? filterDiag.vehicle_filter_rejections
      : 0;
  const stageCounts =
    typeof filterDiag._stage_counts === 'object' && filterDiag._stage_counts !== null
      ? (filterDiag._stage_counts as Record<string, number>)
      : null;

  return (
    <div className="execution-detail">
      {scrapeSkipped ? (
        <div className="scrape-outcome-banner scrape-outcome-banner--neutral">
          <div className="scrape-outcome-banner__header">
            <strong>Scrape skipped</strong>
            <StatusBadge variant="neutral">Skipped</StatusBadge>
          </div>
          <p className="scrape-outcome-banner__message">
            {skipReason || 'Scrape did not run — no fetch was performed.'}
          </p>
        </div>
      ) : null}
      <dl className="detail-grid">
        <dt>Generated URL</dt>
        <dd>
          {generatedUrl ? (
            <TruncatedCell value={generatedUrl} maxWidth={480} />
          ) : (
            '—'
          )}
        </dd>
        <dt>Filters snapshot</dt>
        <dd>
          <pre className="code-block">{JSON.stringify(execution.filters_json, null, 2)}</pre>
        </dd>
        <dt>Mapped params</dt>
        <dd>
          <pre className="code-block">{JSON.stringify(execution.mapped_params, null, 2)}</pre>
        </dd>
        <dt>Unsupported filters</dt>
        <dd>
          {execution.unsupported_filters.length > 0
            ? execution.unsupported_filters.join(', ')
            : 'None'}
        </dd>
        {execution.error ? (
          <>
            <dt>Error</dt>
            <dd className="text-danger">{formatTableError(execution.error)}</dd>
          </>
        ) : null}
      </dl>

      {stageCounts && !scrapeSkipped ? (
        <CollapsiblePanel
          id="execution-pipeline-counts"
          storageScope={`execution-${execution.id}`}
          title="Link pipeline counts"
          defaultExpanded
        >
          <ul className="metric-list">
            {mobileDeExtraction ? (
              <>
                <li>
                  Anchor hrefs scanned:{' '}
                  {mobileDeExtraction.anchorHrefs ?? '—'}
                </li>
                <li>Listing URL candidates: {mobileDeExtraction.listingCandidates}</li>
                <li>
                  Unique listings (ID dedupe):{' '}
                  {mobileDeExtraction.uniqueListings ??
                    stageCounts.after_dedupe ??
                    execution.after_dedupe_count}
                </li>
                <li>Duplicate listing IDs removed: {mobileDeExtraction.duplicateListingIds}</li>
                <li>Non-listing hrefs rejected: {mobileDeExtraction.rejectedNonListing}</li>
              </>
            ) : null}
            <li>Raw extracted: {stageCounts.raw_links ?? execution.raw_links_count}</li>
            <li>After dedupe: {stageCounts.after_dedupe ?? execution.after_dedupe_count}</li>
            <li>After relevance: {stageCounts.after_relevance ?? execution.after_relevance_count}</li>
            <li>New links saved: {execution.new_links_count}</li>
            <li>Existing links: {execution.existing_links_count}</li>
            <li>Passed filters: {stageCounts.after_vehicle_filter ?? execution.after_vehicle_filter_count}</li>
            <li>Rejected by filters: {execution.filtered_out_count}</li>
          </ul>
        </CollapsiblePanel>
      ) : null}

      <CollapsiblePanel
        id="execution-filter-diagnostics"
        storageScope={`execution-${execution.id}`}
        title="Per-filter diagnostics"
        defaultExpanded={false}
      >
        {rejectionCount > 0 && onOpenVehicleFilters ? (
          <p>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenVehicleFilters}>
              Open vehicle filter diagnostics ({rejectionCount} rejected)
            </button>
          </p>
        ) : null}
        {Object.keys(filterDiag).filter((k) => !k.startsWith('_')).length === 0 ? (
          <p className="muted">No per-filter diagnostics recorded.</p>
        ) : (
          <pre className="code-block">
            {JSON.stringify(
              Object.fromEntries(Object.entries(filterDiag).filter(([k]) => !k.startsWith('_'))),
              null,
              2
            )}
          </pre>
        )}
      </CollapsiblePanel>

      <CollapsiblePanel
        id="execution-delivery-summary"
        storageScope={`execution-${execution.id}`}
        title="Delivery summary"
        defaultExpanded={false}
      >
        <ul className="metric-list">
          <li>Send jobs created: {execution.send_jobs_created}</li>
          <li>Deliveries sent: {execution.deliveries_sent}</li>
          <li>Deliveries failed: {execution.deliveries_failed}</li>
        </ul>
      </CollapsiblePanel>
    </div>
  );
}

export default function SearchExecutionsPage() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [searchName, setSearchName] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [debugOnly, setDebugOnly] = useState(false);
  const [items, setItems] = useState<SearchExecution[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<SearchExecution | null>(null);
  const [vehicleFilterDrawerOpen, setVehicleFilterDrawerOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showClearHistoryModal, setShowClearHistoryModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState('');
  const bulk = useBulkSelection<SearchExecution>();
  const fetchGenerationRef = useRef(0);

  const listParams = useMemo<SearchExecutionListParams>(
    () => ({
      search_name: searchName || undefined,
      source: sourceFilter || undefined,
      status: statusFilter || undefined,
      debug_only: debugOnly || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [searchName, sourceFilter, statusFilter, debugOnly, page]
  );

  const fetchExecutions = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    setError(null);
    const data = await api.searchExecutions.list(listParams);
    if (generation !== fetchGenerationRef.current) return;
    setItems(data.items);
    setTotal(data.total);
  }, [listParams]);

  const clearPipelineState = useCallback(() => {
    fetchGenerationRef.current += 1;
    setItems([]);
    setTotal(0);
    setSelectedId(null);
    setSelectedDetail(null);
    setVehicleFilterDrawerOpen(false);
    setError(null);
    bulk.clearSelection();
  }, [bulk]);

  const { autoRefresh, initialLoading } = useAutoRefreshPolling({
    hasData: () => items.length > 0,
    onWorkspaceChange: clearPipelineState,
    load: async () => {
      try {
        await fetchExecutions();
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to load search executions';
        setError(message);
        throw e instanceof Error ? e : new Error(message);
      }
    },
    autoRefresh: {
      intervalMs: 10000,
      deps: [listParams],
      storageKey: 'search-executions-auto-refresh',
    },
  });

  useEffect(() => {
    const executionId = searchParams.get('execution_id');
    if (executionId) {
      setSelectedId(executionId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedDetail(null);
      return;
    }
    void api.searchExecutions.get(selectedId).then(setSelectedDetail).catch(() => setSelectedDetail(null));
  }, [selectedId, autoRefresh.lastUpdatedAt]);

  const columns = useMemo<DataTableColumn<SearchExecution>[]>(
    () => [
      {
        id: 'search_name',
        header: 'Search',
        render: (row) => row.search_name || '—',
      },
      {
        id: 'source_name',
        header: 'Source',
        render: (row) => {
          const label = pipelineSourceLabel(row);
          if (isOverallPipelineExecution(row)) {
            return <span className="muted">{label}</span>;
          }
          return <SourceChips sources={[label]} variant="compact" />;
        },
      },
      {
        id: 'status',
        header: 'Status',
        render: (row) => (
          <StatusBadge variant={statusVariant(row.status)}>{row.status}</StatusBadge>
        ),
      },
      {
        id: 'seed_url',
        header: 'URL',
        render: (row) => <TruncatedCell value={row.seed_url || '—'} maxWidth={200} />,
      },
      {
        id: 'raw_links_count',
        header: 'Raw',
        render: (row) => row.raw_links_count,
      },
      {
        id: 'passed_filter_count',
        header: 'Passed',
        render: (row) => row.after_vehicle_filter_count,
      },
      {
        id: 'rejected_filter_count',
        header: 'Rejected',
        render: (row) => row.filtered_out_count,
      },
      {
        id: 'existing_links_count',
        header: 'Existing',
        render: (row) => row.existing_links_count,
      },
      {
        id: 'new_links_count',
        header: 'New',
        render: (row) => row.new_links_count,
      },
      {
        id: 'send_jobs_created',
        header: 'Send jobs',
        render: (row) => row.send_jobs_created,
      },
      {
        id: 'timestamp',
        header: 'When',
        render: (row) => formatTime(row.started_at || row.created_at),
      },
    ],
    []
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageKeys = useMemo(() => filterValidUuids(items.map((i) => i.id)), [items]);

  const runBulkDelete = useCallback(async () => {
    const ids = filterValidUuids(Array.from(bulk.selectedKeys));
    if (!ids.length) {
      setBulkDeleteError('Selected rows do not have valid execution IDs');
      return;
    }
    setBulkBusy(true);
    setBulkDeleteError('');
    try {
      const result = await api.searchExecutions.bulkDelete(ids);
      toast(`Deleted ${result.deleted ?? 0} execution(s)`, 'success');
      bulk.clearSelection();
      setShowBulkDeleteModal(false);
      await autoRefresh.refresh();
    } catch (e) {
      setBulkDeleteError(formatApiError(e, 'Delete failed'));
      throw e;
    } finally {
      setBulkBusy(false);
    }
  }, [autoRefresh, bulk, toast]);

  return (
    <>
      <PageHeader
        title="Pipeline diagnostics"
        subtitle="Search execution history — see whether each source was queried, how filters mapped, and why nothing was sent."
      >
        <AutoRefreshToolbar control={autoRefresh} intervalLabel="Every 10s" />
      </PageHeader>
      <AdminPageSections>
        <ScrapeAnalyticsPanel refreshSignal={autoRefresh.lastUpdatedAt} />

        <CollapsiblePanel
          id="execution-list"
          storageScope={PANEL_SCOPE}
          title="Search execution history"
          badge={initialLoading ? '…' : total}
          defaultExpanded
        >
          <Toolbar>
            <label className="filter-field">
              <span className="filter-label">Search name</span>
              <input
                type="search"
                value={searchName}
                onChange={(e) => {
                  setPage(1);
                  setSearchName(e.target.value);
                }}
                placeholder="Filter by search name"
              />
            </label>
            <FilterSelect label="Source" htmlFor="execution-source-filter">
              <select
                id="execution-source-filter"
                className="filter-select"
                value={sourceFilter}
                onChange={(e) => {
                  setPage(1);
                  setSourceFilter(e.target.value);
                }}
              >
                {SOURCE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </FilterSelect>
            <FilterSelect label="Status" htmlFor="execution-status-filter">
              <select
                id="execution-status-filter"
                className="filter-select"
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value);
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </FilterSelect>
            <Toggle
              compact
              checked={debugOnly}
              onChange={(next) => {
                setPage(1);
                setDebugOnly(next);
              }}
              label="DEBUG searches only"
            />
          </Toolbar>

          {error ? <ErrorBanner message={error} onDismiss={() => setError(null)} /> : null}

          {initialLoading && items.length === 0 ? (
            <EmptyState title="Loading executions…" description="Fetching pipeline diagnostics from the API." />
          ) : items.length === 0 ? (
            <EmptyState
              title="No search executions yet"
              description="Run a scrape (scheduled or manual) to populate execution history. Use the debug seed script to create DEBUG vehicle searches."
            />
          ) : (
            <>
              <BulkActionBar
                selectedCount={bulk.selectedCount}
                totalFiltered={items.length}
                totalAll={total}
                onClear={bulk.clearSelection}
                onSelectPage={() => bulk.togglePage(pageKeys)}
                onSelectFiltered={() => bulk.selectFiltered(pageKeys)}
                loading={bulkBusy}
              >
                <BulkActionButton
                  variant="danger"
                  loading={bulkBusy}
                  disabled={bulk.selectedCount === 0}
                  onClick={() => {
                    setBulkDeleteError('');
                    setShowBulkDeleteModal(true);
                  }}
                >
                  Delete selected
                </BulkActionButton>
                <BulkActionButton
                  variant="danger"
                  loading={bulkBusy}
                  onClick={() => setShowClearHistoryModal(true)}
                >
                  Clear history
                </BulkActionButton>
              </BulkActionBar>
              <DataTable
                columns={columns}
                rows={items}
                getRowKey={(row) => row.id}
                onRowClick={(row) => setSelectedId(row.id === selectedId ? null : row.id)}
                columnStorageKey="search-executions"
                selection={{
                  isSelected: bulk.isSelected,
                  onToggleRow: bulk.toggleRow,
                  pageKeys,
                  pageAllSelected: bulk.pageAllSelected(pageKeys),
                  pageSomeSelected: bulk.pageSomeSelected(pageKeys),
                  onTogglePage: () => bulk.togglePage(pageKeys),
                }}
                toolbar={{
                  searchPlaceholder: 'Filter visible rows…',
                  onRefresh: () => void autoRefresh.refresh(),
                  onExportCsv: () => exportTableCsv(columns, items, 'search-executions.csv'),
                }}
              />
              <Pagination page={page} pages={totalPages} total={total} onPage={setPage} />
            </>
          )}
        </CollapsiblePanel>

        {selectedDetail ? (
          <CollapsiblePanel
            id="execution-detail"
            storageScope={PANEL_SCOPE}
            title={`Execution detail — ${selectedDetail.search_name || selectedDetail.source_name}`}
            defaultExpanded
          >
            <ExecutionDetailPanel
              execution={selectedDetail}
              onOpenVehicleFilters={() => setVehicleFilterDrawerOpen(true)}
            />
          </CollapsiblePanel>
        ) : null}

        {selectedDetail && vehicleFilterDrawerOpen ? (
          <VehicleFilterDiagnosticsDrawer
            executionId={selectedDetail.id}
            executionLabel={selectedDetail.search_name || selectedDetail.source_name}
            onClose={() => setVehicleFilterDrawerOpen(false)}
          />
        ) : null}
      </AdminPageSections>

      {showClearHistoryModal ? (
        <PipelineClearHistoryModal
          olderThanDays={7}
          debugOnly
          onClose={() => setShowClearHistoryModal(false)}
          onDone={(message) => {
            toast(message, 'success');
            void autoRefresh.refresh();
          }}
        />
      ) : null}

      {showBulkDeleteModal ? (
        <PipelineBulkDeleteModal
          selectedCount={bulk.selectedCount}
          submitting={bulkBusy}
          error={bulkDeleteError}
          onClose={() => {
            if (!bulkBusy) setShowBulkDeleteModal(false);
          }}
          onConfirm={runBulkDelete}
        />
      ) : null}
    </>
  );
}
